import axios from "axios";
import React, { FC, useEffect, useState, CSSProperties, useRef } from "react";
import { useNavigate } from "react-router-dom";
import config from "../config";
import { useAuth0 } from "@auth0/auth0-react";
import logo_text_dark from "../assets/logoB.png";
import logo_text_light from "../assets/logo.png";
import logo_l from "../assets/image_light.png";
import logo_d from "../assets/image_dark.png";
import shell from "../assets/shell.png";
import dechra_dark from "../assets/dechra_blue.png";
import dechra_light from "../assets/dechra_white.png";
import UserSelectionPopup from "../components/UserSelectionPopup";
import { Theme, getThemeColors } from "../theme/colors";
import { SidebarHeader } from "../components/sidebar/SidebarHeader";
import { NewChatButton } from "../components/sidebar/NewChatButton";
import { ChatHistory } from "../components/sidebar/ChatHistory";
import { ChatOptionsMenu } from "../components/sidebar/ChatOptionsMenu";
import { SidebarFooter } from "../components/sidebar/SidebarFooter";
import { DevMenuPopup } from "../components/sidebar/DevMenuPopup";
import { SharedDetailsPopup } from "../components/sidebar/SharedDetailsPopup";

interface SidebarProps {
  username: string;
  colorMode: string;
  setColorMode: (mode: Theme) => void;
  isCollapsed: boolean;
  setIsCollapsed: (isOpen: boolean) => void;
  onDevNumberChange?: (devNumber: number) => void;
}

export const isDechraFn = (username: string) => {
  return username.toLowerCase().includes("dechra");
};

export const isShellFn = (username: string) => {
  return (
    username.toLowerCase().includes("shell.com") ||
    username === "abhishek.anand@straive.com" ||
    username === "demo.cg1@oneadvisor.ai"
  );
};

export const getLogoImages = (username: string, colorMode: string) => {
  const isDechra = isDechraFn(username);
  const isShell = isShellFn(username);

  if (isDechra) {
    return {
      collapsed: colorMode === "dark" ? dechra_light : dechra_dark,
      expanded: colorMode === "dark" ? dechra_light : dechra_dark,
    };
  } else if (isShell) {
    return {
      collapsed: colorMode === "dark" ? shell : shell,
      expanded: colorMode === "dark" ? shell : shell,
    };
  } else {
    return {
      collapsed: colorMode === "dark" ? logo_l : logo_d,
      expanded: colorMode === "dark" ? logo_text_light : logo_text_dark,
    };
  }
};

const Sidebar: FC<SidebarProps> = ({
  username,
  colorMode,
  setColorMode,
  isCollapsed,
  setIsCollapsed,
  onDevNumberChange,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [chatList, setChatList] = useState<
    {
      id: string;
      question: string;
      display_name: string;
      notes: string;
      thumbs: boolean | null;
      feedback: string;
      is_public: boolean | null;
      latest_created_at: string | number | Date;
      created_at: string | number | Date;
      is_shared: boolean;
    }[]
  >([]);
  const [sharedChatList, setSharedChatList] = useState<
    {
      id: string;
      question: string;
      display_name: string;
      notes: string;
      thumbs: boolean | null;
      feedback: string;
      is_public: boolean | null;
      latest_created_at: string | number | Date;
      created_at: string | number | Date;
      is_shared: boolean;
    }[]
  >([]);
  const [sharedWithUserChats, setSharedWithUserChats] = useState<
    {
      id: string;
      question: string;
      display_name: string;
      notes: string;
      thumbs: boolean | null;
      feedback: string;
      is_public: boolean | null;
      latest_created_at: string | number | Date;
      created_at: string | number | Date;
      is_shared: boolean;
    }[]
  >([]);
  const navigate = useNavigate();
  const { logout } = useAuth0();
  const [devMode, setDevMode] = useState<boolean>(false);
  const [devNumber, setDevNumber] = useState<number>(0);
  const [refresh, setRefresh] = useState<number>(0);
  const [activeOptions, setActiveOptions] = useState<[string | null, boolean]>([
    null,
    false,
  ]);
  const optionsRef = useRef<HTMLDivElement | null>(null);
  const [optionsPosition, setOptionsPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const [renamingChatId, setRenamingChatId] = useState<string | null>(null);
  const [newDisplayName, setNewDisplayName] = useState<string>("");

  // User selection popup state
  const [showUserSelectionPopup, setShowUserSelectionPopup] =
    useState<boolean>(false);
  const [availableUsers, setAvailableUsers] = useState<
    { username: string; id: number }[]
  >([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(false);
  const [selectedChatForSharing, setSelectedChatForSharing] = useState<
    string | null
  >(null);
  const [showDevMenuPopup, setShowDevMenuPopup] = useState<boolean>(false);
  const [showSharedDetailsPopup, setShowSharedDetailsPopup] =
    useState<boolean>(false);
  const [sharedWithUsersList, setSharedWithUsersList] = useState<string[]>([]);
  const [detailsSourceCategory, setDetailsSourceCategory] = useState<
    "shared-chats" | "shared-with-me" | null
  >(null);

  const handleDelete = async (chatId: string) => {
    console.log("Delete clicked for chat:", chatId);
    setActiveOptions([null, false]);
    setIsLoading(true);
    try {
      await axios.post(`${config.api.baseUrl}/chat/question_feedback`, {
        question_id: chatId,
        username: username,
        is_public: "FALSE",
      });

      setChatList([]);
      setSharedChatList([]);
      setSharedWithUserChats([]);
      setRefresh((prev) => prev + 1);
    } catch (error) {
      console.error("Error deleting chat:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChatShare = async (chatId: string) => {
    console.log("Sharing clicked for chat:", chatId);
    setActiveOptions([null, false]);
    setSelectedChatForSharing(chatId);
    setUsersLoading(true);

    try {
      // Fetch available users
      const response = await axios.post(
        `${config.api.baseUrl}/chat/user_list`,
        {
          username: username,
        }
      );

      setAvailableUsers(response.data.users || []);
      setShowUserSelectionPopup(true);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleShareWithSelectedUsers = async (selectedUsers: string[]) => {
    if (!selectedChatForSharing) return;

    setIsLoading(true);
    try {
      await axios.post(`${config.api.baseUrl}/chat/share_chat`, {
        chat_id: selectedChatForSharing,
        username: username,
        shared_with_users: selectedUsers,
      });

      setChatList([]);
      setSharedChatList([]);
      setSharedWithUserChats([]);
      setRefresh((prev) => prev + 1);
      setShowUserSelectionPopup(false);
      setSelectedChatForSharing(null);
      navigate(`/chat/${selectedChatForSharing}`);
    } catch (error) {
      console.error("Error sharing chat:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseUserSelectionPopup = () => {
    setShowUserSelectionPopup(false);
    setSelectedChatForSharing(null);
    setAvailableUsers([]);
  };

  const handleChatUnShare = async (chatId: string) => {
    console.log("UnSharing clicked for chat:", chatId);
    setActiveOptions([null, false]);
    setIsLoading(true);
    try {
      await axios.post(`${config.api.baseUrl}/chat/unshare_chat`, {
        chat_id: chatId,
        username: username,
      });

      setChatList([]);
      setSharedChatList([]);
      setSharedWithUserChats([]);
      setRefresh((prev) => prev + 1);
    } catch (error) {
      console.error("Error unsharing chat:", error);
    } finally {
      setIsLoading(false);
      navigate(`/chat/${chatId}`);
      setRefresh((prev) => prev + 1);
    }
  };

  const handleChatDetails = async (
    chatId: string,
    source?: "shared-chats" | "shared-with-me"
  ) => {
    console.log("Details clicked for chat:", chatId);
    setActiveOptions([null, false]);
    setDetailsSourceCategory(source ?? null);
    // setIsLoading(true);
    try {
      const response = await axios.post(
        `${config.api.baseUrl}/chat/shared_details`,
        {
          chat_id: chatId,
          username: username,
        }
      );
      const data = response.data;
      const shared = data?.shared;
      const users: string[] = Array.isArray(shared)
        ? shared
        : typeof shared === "string" && shared
        ? [shared]
        : [];
      setSharedWithUsersList(users);
      setShowSharedDetailsPopup(true);
    } catch (error) {
      console.error("Error getting details chat:", error);
    } finally {
      // setIsLoading(false);
    }
  };

  const handleRename = (chatId: string) => {
    console.log("Rename clicked for chat:", chatId);
    setRenamingChatId(chatId);
    const chat = chatList.find((chat) => chat.id === chatId);
    setNewDisplayName(chat ? chat.display_name || chat.question : ""); // Set initial value of the input field
    setActiveOptions([null, false]);
  };

  const submitRename = async (chatId: string) => {
    setIsLoading(true);
    try {
      await axios.post(`${config.api.baseUrl}/chat/question_feedback`, {
        question_id: chatId,
        username: username,
        display_name: newDisplayName,
      });
      setRenamingChatId(null);
      setChatList([]);
      setSharedChatList([]);
      setSharedWithUserChats([]);
      setRefresh((prev) => prev + 1);
    } catch (error) {
      console.error("Error renaming chat:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (renamingChatId) {
      submitRename(renamingChatId);
    }
  };

  const handleRenameCancel = () => {
    setRenamingChatId(null); // Cancel renaming
  };

  useEffect(() => {
    const fetchChatList = async () => {
      try {
        const response = await axios.post(
          `${config.api.baseUrl}/chat/chat_list`,
          { username }
        );
        if (response.data) {
          const clearedTime =
            new Date(response.data?.cleared_at).getTime() ?? 0;

          // Filter and sort regular chats (not shared)
          const regularChats = response.data.rows
            .filter(
              (row: {
                ids: string;
                is_public: boolean | null;
                created_at: string | number | Date;
                is_shared: boolean | null;
              }) => {
                const isPublic = row.is_public !== false;
                const isNotShared = row.is_shared !== true;
                const createdAt = new Date(row.created_at).getTime();
                const afterClear = createdAt > clearedTime;
                return isPublic && afterClear && isNotShared;
              }
            )
            .sort(
              (
                a: { latest_created_at: string | number | Date },
                b: { latest_created_at: string | number | Date }
              ) => {
                const dateA = new Date(a.latest_created_at).getTime();
                const dateB = new Date(b.latest_created_at).getTime();
                return dateB - dateA;
              }
            );

          // Filter and sort shared chats
          const seenIds = new Set<string>();

          const sharedChats = response.data.rows
            .filter(
              (row: {
                ids: string;
                is_public: boolean | null;
                created_at: string | number | Date;
                is_shared: boolean | null;
              }) => {
                const isPublic = row.is_public !== false;
                const isShared = row.is_shared === true;
                const createdAt = new Date(row.created_at).getTime();
                const afterClear = createdAt > clearedTime;
                return isPublic && afterClear && isShared;
              }
            )
            .sort(
              (
                a: { latest_created_at: string | number | Date },
                b: { latest_created_at: string | number | Date }
              ) => {
                const dateA = new Date(a.latest_created_at).getTime();
                const dateB = new Date(b.latest_created_at).getTime();
                return dateB - dateA;
              }
            )
            .filter((row: { ids: string }) => {
              if (seenIds.has(row.ids)) {
                return false; // already saw this id → skip
              }
              seenIds.add(row.ids); // first time → keep
              return true;
            });

          const sharedWithUserChats = response.data.shared_with_user.sort(
            (
              a: { latest_created_at: string | number | Date },
              b: { latest_created_at: string | number | Date }
            ) => {
              const dateA = new Date(a.latest_created_at).getTime();
              const dateB = new Date(b.latest_created_at).getTime();
              return dateB - dateA;
            }
          );

          setChatList(regularChats);
          setSharedChatList(sharedChats);
          setSharedWithUserChats(sharedWithUserChats);

          setDevMode(response.data?.dev_flag > 0);
          setDevNumber(response.data?.dev_flag);

          // Call the callback to notify parent component
          if (onDevNumberChange) {
            onDevNumberChange(response.data?.dev_flag);
          }

          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching chat list:", error);
        setIsLoading(false);
      }
    };

    fetchChatList();
  }, [username, refresh, onDevNumberChange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        optionsRef.current &&
        !optionsRef.current.contains(event.target as Node)
      ) {
        setActiveOptions([null, false]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [setIsCollapsed]);

  const onChatCreate = (id: string, shared: boolean) => {
    if (shared) {
      navigate(`/chat/${id}/1`);
    } else {
      navigate(`/chat/${id}`);
    }

    setRefresh((prev) => prev + 1);
  };

  const clearChatHistory = async () => {
    console.log(chatList);
    setIsLoading(true);
    try {
      await axios.post(`${config.api.baseUrl}/chat/clear_history`, {
        username: username,
      });

      setChatList([]);
      setSharedChatList([]);
      setSharedWithUserChats([]);
      setRefresh((prev) => prev + 1);
    } catch (error) {
      console.error("Error sending chat history delete:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleColorMode = () => {
    if (colorMode === "dark") {
      setColorMode("light");
    } else {
      setColorMode("dark");
    }
  };

  const handleOptionsClick = (
    e: React.MouseEvent,
    chatId: string,
    isShared: boolean
  ) => {
    e.stopPropagation();
    setActiveOptions([activeOptions[0] === chatId ? null : chatId, isShared]);
    const rect = e.currentTarget.getBoundingClientRect();
    setOptionsPosition({
      top: rect.top + window.scrollY + 15, // Keep the top as where it was clicked
      left: rect.right + window.scrollX - 168, // Adjust to position right of the clicked element with some spacing
    });
  };

  const currentColors = getThemeColors(username, colorMode as Theme);

  const sidebarStyle: CSSProperties = {
    width: isCollapsed ? "3rem" : "16rem",
    height: "100vh",
    padding: "0.5rem",
    color: currentColors.text,
    backgroundColor: currentColors.sidebarBg,
    zIndex: 1,
    transition: "all ease 0.5s",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden",
    boxSizing: "border-box",
  };

  const mainActionsStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    flexGrow: 1,
  };

  return (
    <div style={sidebarStyle}>
      <SidebarHeader
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        username={username}
        colorMode={colorMode}
      />

      <div style={mainActionsStyle}>
        <NewChatButton
          isCollapsed={isCollapsed}
          onClick={() => onChatCreate("0", false)}
          username={username}
          colorMode={colorMode}
        />

        {!isCollapsed && (
          <ChatHistory
            isLoading={isLoading}
            chatList={chatList}
            sharedChatList={sharedChatList}
            sharedWithUserChats={sharedWithUserChats}
            username={username}
            colorMode={colorMode}
            renamingChatId={renamingChatId}
            newDisplayName={newDisplayName}
            setNewDisplayName={setNewDisplayName}
            handleRenameSubmit={handleRenameSubmit}
            handleRenameCancel={handleRenameCancel}
            onChatCreate={onChatCreate}
            handleOptionsClick={handleOptionsClick}
          />
        )}
      </div>

      <ChatOptionsMenu
        activeOptions={activeOptions}
        optionsPosition={optionsPosition}
        devNumber={devNumber}
        sharedChatList={sharedChatList}
        sharedWithUserChats={sharedWithUserChats}
        handleRename={handleRename}
        handleDelete={handleDelete}
        handleChatShare={handleChatShare}
        handleChatUnShare={handleChatUnShare}
        handleChatDetails={handleChatDetails}
        username={username}
        colorMode={colorMode}
      />

      <SidebarFooter
        isCollapsed={isCollapsed}
        colorMode={colorMode}
        username={username}
        devMode={devMode}
        toggleColorMode={toggleColorMode}
        handleLogout={() =>
          logout({ logoutParams: { returnTo: window.location.origin } })
        }
        setShowDevMenuPopup={setShowDevMenuPopup}
      />

      <UserSelectionPopup
        isOpen={showUserSelectionPopup}
        onClose={handleCloseUserSelectionPopup}
        onShare={handleShareWithSelectedUsers}
        users={availableUsers}
        isLoading={usersLoading}
        colorMode={colorMode as Theme}
        username={username}
      />

      <SharedDetailsPopup
        isOpen={showSharedDetailsPopup}
        onClose={() => {
          setShowSharedDetailsPopup(false);
          setDetailsSourceCategory(null);
        }}
        sharedWithUsers={sharedWithUsersList}
        sourceCategory={detailsSourceCategory}
        username={username}
        colorMode={colorMode}
      />

      <DevMenuPopup
        isOpen={showDevMenuPopup}
        onClose={() => setShowDevMenuPopup(false)}
        clearChatHistory={clearChatHistory}
        username={username}
        colorMode={colorMode}
        devNumber={devNumber}
      />
    </div>
  );
};

export default Sidebar;
